import React, { useState } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { useLancamentoPerdasLaboratorio } from '../../../contexts/MP/LancamentoPerdasLaboratorioContext';

const ModalLancamentoPerdasLaboratorio = () => {
    const {
        showModal,
        isLoading,
        showSuccess,
        
        pecas,
        amostra,
        motivos,
        reqMaterial,
        errosLancamento,
        pecasExtrasDisponiveis,
        
        observacao,
        multiplicadores,
        pecasExtras,
        
        isBatchMode,
        quantidadeBatch,
        motivoBatch,
        multiplicadorBatch,
        
        fecharModal,
        processarLancamentoPerdas,
        toggleBatchMode,
        
        adicionarPecaExtra,
        removerPecaExtra,
        atualizarPecaExtra,
        atualizarMultiplicador,
        
        handleObservacaoChange,
        handleQuantidadeBatchChange,
        handleMotivoBatchChange,
        handleMultiplicadorBatchChange,
    } = useLancamentoPerdasLaboratorio();

    // Estado para forçar re-renderização quando quantidades mudarem
    const [, setUpdateTrigger] = useState(0);

    // Handler para atualização de quantidade que força re-renderização
    const handleQuantidadeChange = (peca, novaQtd) => {
        peca.qtdeLancar = novaQtd;
        setUpdateTrigger(prev => prev + 1);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        processarLancamentoPerdas();
    };

    // Calcular total de peças (principais + extras selecionadas) em modo lote
    const calcularTotalPecas = () => {
        if (isBatchMode) {
            const pecasExtrasSelecionadas = pecasExtras.filter(pe => pe.pecaSelecionada).length;
            return pecas.length + pecasExtrasSelecionadas;
        }
        return pecas.length;
    };

    // Calcular quantidade total de registros que serão criados
    const calcularTotalRegistros = () => {
        let total = 0;
        
        if (isBatchMode) {
            // Modo lote: multiplicador batch × número total de peças (principais + extras selecionadas)
            const totalPecas = calcularTotalPecas();
            total = (multiplicadorBatch || 1) * totalPecas;
        } else {
            // Modo individual: Peças principais
            pecas.forEach((peca, index) => {
                const qtd = parseFloat(peca.qtdeLancar) || 0;
                if (qtd > 0) {
                    const mult = multiplicadores[index] || 1;
                    total += mult;
                }
            });
            
            // Peças extras (apenas em modo individual)
            pecasExtras.forEach(pecaExtra => {
                const qtd = parseFloat(pecaExtra.qtdeLancar) || 0;
                if (qtd > 0) {
                    const mult = pecaExtra.multiplicador || 1;
                    total += mult;
                }
            });
        }
        
        return total;
    };

    // Calcular quantidade total de material
    const calcularQuantidadeTotalMaterial = () => {
        let total = 0;
        
        if (isBatchMode) {
            // Modo lote: quantidade batch × multiplicador batch × número total de peças (principais + extras selecionadas)
            const qtdBatch = parseFloat(quantidadeBatch) || 0;
            const multBatch = multiplicadorBatch || 1;
            const totalPecas = calcularTotalPecas();
            total = qtdBatch * multBatch * totalPecas;
        } else {
            // Modo individual: Peças principais
            pecas.forEach((peca, index) => {
                const qtd = parseFloat(peca.qtdeLancar) || 0;
                const mult = multiplicadores[index] || 1;
                total += qtd * mult;
            });
            
            // Peças extras (apenas em modo individual)
            pecasExtras.forEach(pecaExtra => {
                const qtd = parseFloat(pecaExtra.qtdeLancar) || 0;
                const mult = pecaExtra.multiplicador || 1;
                total += qtd * mult;
            });
        }
        
        return total;
    };

    if (showSuccess) {
        return (
            <Modal 
                size="lg" 
                backdrop="static" 
                show={showModal} 
                onHide={fecharModal} 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Lançamento de Perdas - Sucesso</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="success">
                        <Alert.Heading>Lançamento realizado com sucesso!</Alert.Heading>
                        <p>
                            {reqMaterial.includes(',') ? 'Requisições de Material:' : 'Requisição de Material:'} <strong>{reqMaterial}</strong>
                        </p>
                        <hr />
                        <p className="mb-0">
                            As perdas foram registradas no sistema.
                            <br />Você pode fechar esta janela.
                        </p>
                        
                        {Array.isArray(errosLancamento) && errosLancamento.length > 0 && (
                            <>
                                <hr />
                                <Alert variant="warning" className="mt-3">
                                    <Alert.Heading>Alguns lançamentos falharam:</Alert.Heading>
                                    <ul className="mb-0">
                                        {errosLancamento.map((erro, index) => (
                                            <li key={index}>{erro}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            </>
                        )}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={fecharModal}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal 
            size="xl" 
            backdrop="static" 
            show={showModal} 
            onHide={fecharModal} 
            centered
        >
            <Modal.Header closeButton>
                <div className="w-100 d-flex justify-content-between align-items-center me-3">
                    <Modal.Title>
                        Lançamento de Perdas - Laboratório
                        <span className={`ms-2 badge ${isBatchMode ? 'bg-info' : 'bg-secondary'}`}>
                            {isBatchMode ? 'Modo Lote' : 'Modo Individual'}
                        </span>
                    </Modal.Title>
                    <Button 
                        variant={isBatchMode ? "warning" : "info"}
                        size="md"
                        onClick={toggleBatchMode}
                        disabled={isLoading}
                        className="fw-bold"
                    >
                        <i className="bi bi-arrow-left-right me-2"></i>
                        Alterar para lançamento {isBatchMode ? 'individual' : 'em lote'}
                    </Button>
                </div>
            </Modal.Header>
            
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="mb-4 p-3 bg-light rounded">
                        <h5>Informações do Material</h5>
                        <div className="row">
                            <div className="col-md-6">
                                <strong>Material:</strong> {amostra.Material || amostra.material || 'N/A'}
                            </div>
                            <div className="col-md-6">
                                <strong>Cor Material:</strong> {amostra.CorMaterial || amostra.cormaterial || 'N/A'}
                            </div>
                            <div className="col-md-6">
                                <strong>Descrição:</strong> {amostra.DescricaoMaterial || amostra.descricaomaterial || 'N/A'}
                            </div>
                            <div className="col-md-6">
                                <strong>Fornecedor:</strong> {amostra.NomeCliFor || amostra.nomeCliFor || 'N/A'}
                            </div>
                        </div>
                    </div>

                    {isBatchMode ? (
                        <>
                            <Alert variant="info" className="mb-4">
                                <Alert.Heading>
                                    <i className="bi bi-info-circle me-2"></i>
                                    Lançamento em Lote
                                </Alert.Heading>
                                <p className="mb-0">
                                    Neste modo, a mesma <strong>quantidade</strong>, <strong>motivo</strong> e <strong>multiplicador</strong> serão aplicados para todas as peças listadas abaixo.
                                </p>
                            </Alert>

                            <div className="mb-4 p-4 border border-primary rounded bg-light">
                                <h5 className="text-primary mb-3">Informações do Lote</h5>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <Form.Group>
                                            <Form.Label>
                                                <strong>Quantidade de Perda (por peça)</strong>
                                                <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={quantidadeBatch}
                                                onChange={handleQuantidadeBatchChange}
                                                placeholder="Ex: 2.5"
                                                disabled={isLoading}
                                                required
                                                className="form-control-lg"
                                            />
                                            <Form.Text className="text-muted">
                                                Esta quantidade será aplicada para TODAS as peças
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group>
                                            <Form.Label>
                                                <strong>Motivo da Perda</strong>
                                                <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select 
                                                value={motivoBatch}
                                                onChange={handleMotivoBatchChange}
                                                disabled={isLoading}
                                                required
                                                className="form-select-lg"
                                            >
                                                <option value={-1}>Selecione o motivo...</option>
                                                {Array.isArray(motivos) && motivos.map((motivo) => (
                                                    <option key={motivo.IdMotivo} value={motivo.IdMotivo}>
                                                        {motivo.Motivo}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Este motivo será aplicado para TODAS as peças
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group>
                                            <Form.Label>
                                                <strong>Multiplicador</strong>
                                                <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={multiplicadorBatch}
                                                onChange={handleMultiplicadorBatchChange}
                                                disabled={isLoading}
                                                required
                                                className="form-control-lg"
                                            />
                                            <Form.Text className="text-muted">
                                                Cria N registros para cada peça
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                </div>

                                {quantidadeBatch && parseFloat(quantidadeBatch) > 0 && calcularTotalPecas() > 0 && (
                                    <div className="mt-3 p-3 bg-white border border-info rounded">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong className="text-info">Total de Material a ser Lançado:</strong>
                                                <br />
                                                <small className="text-muted">
                                                    {quantidadeBatch} × {multiplicadorBatch} multiplicador × {calcularTotalPecas()} peças
                                                </small>
                                            </div>
                                            <div className="text-center mx-3">
                                                <strong className="text-info">Registros:</strong>
                                                <br />
                                                <span className="fs-4 fw-bold text-info">
                                                    {calcularTotalRegistros()}
                                                </span>
                                            </div>
                                            <div className="text-end">
                                                <span className="fs-3 fw-bold text-info">
                                                    {calcularQuantidadeTotalMaterial().toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </span>
                                                <small className="d-block text-muted">unidades</small>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mb-4 p-3 bg-info bg-opacity-10 border border-info rounded">
                                <div className="row align-items-center">
                                    <div className="col-md-6">
                                        <h6 className="mb-0 text-info">
                                            Total de Registros a Criar
                                        </h6>
                                        <small className="text-muted">
                                            Considerando multiplicadores
                                        </small>
                                    </div>
                                    <div className="col-md-3 text-center">
                                        <span className="fs-4 fw-bold text-info">
                                            {calcularTotalRegistros()}
                                        </span>
                                        <small className="d-block text-muted">registros</small>
                                    </div>
                                    <div className="col-md-3 text-end">
                                        <span className="fs-4 fw-bold text-info">
                                            {calcularQuantidadeTotalMaterial().toLocaleString('pt-BR', { 
                                                minimumFractionDigits: 0, 
                                                maximumFractionDigits: 2 
                                            })}
                                        </span>
                                        <small className="d-block text-muted">unidades totais</small>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="mb-4 p-3 bg-info bg-opacity-10 border border-info rounded">
                                <div className="row align-items-center">
                                    <div className="col-md-6">
                                        <h6 className="mb-0 text-info">
                                            Total de Registros a Criar
                                        </h6>
                                        <small className="text-muted">
                                            Considerando multiplicadores
                                        </small>
                                    </div>
                                    <div className="col-md-3 text-center">
                                        <span className="fs-4 fw-bold text-info">
                                            {calcularTotalRegistros()}
                                        </span>
                                        <small className="d-block text-muted">registros</small>
                                    </div>
                                    <div className="col-md-3 text-end">
                                        <span className="fs-4 fw-bold text-info">
                                            {calcularQuantidadeTotalMaterial().toLocaleString('pt-BR', { 
                                                minimumFractionDigits: 0, 
                                                maximumFractionDigits: 2 
                                            })}
                                        </span>
                                        <small className="d-block text-muted">unidades totais</small>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="mb-4">
                        <h5 className="mb-3">Peças</h5>

                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th style={{ width: '80px' }}>Ações</th>
                                    <th>Peça</th>
                                    <th>Metro Fornec.</th>
                                    <th>Metragem Relógio</th>
                                    <th>Motivo da Perda</th>
                                    <th>Qtd. Perda</th>
                                    <th>Multiplicador</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Peças de Amostragem */}
                                {Array.isArray(pecas) && pecas.map((peca, index) => {
                                    const isReadOnly = isBatchMode;
                                    return (
                                        <tr key={`amostragem-${index}`}>
                                            <td></td>
                                            <td><strong>{peca.peca}</strong></td>
                                            <td>{peca.metroForn}</td>
                                            <td>{peca.relogio}</td>
                                            <td>
                                                {isReadOnly ? (
                                                    <span className="text-muted">Aplicado em lote</span>
                                                ) : (
                                                    <Form.Select 
                                                        onChange={(e) => {
                                                            const novoMotivo = e.target.value;
                                                            peca.motivoSelecionado = novoMotivo;
                                                            setUpdateTrigger(prev => prev + 1);
                                                        }}
                                                        value={peca.motivoSelecionado || -1}
                                                        disabled={isLoading}
                                                        style={{ minWidth: '150px' }}
                                                    >
                                                        <option value={-1}>Selecione...</option>
                                                        {Array.isArray(motivos) && motivos.map((motivo) => (
                                                            <option key={motivo.IdMotivo} value={motivo.IdMotivo}>
                                                                {motivo.Motivo}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                )}
                                            </td>
                                            <td>
                                                {isReadOnly ? (
                                                    <span className="text-muted">{quantidadeBatch || '-'}</span>
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        onChange={(e) => {
                                                            const novaQtd = parseFloat(e.target.value) || 0;
                                                            handleQuantidadeChange(peca, novaQtd);
                                                        }}
                                                        style={{ width: '100px' }}
                                                        disabled={isLoading}
                                                        placeholder="0"
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {isReadOnly ? (
                                                    <span className="text-muted">{multiplicadorBatch || 1}</span>
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        value={multiplicadores[index] || 1}
                                                        onChange={(e) => {
                                                            const novoMult = parseInt(e.target.value) || 1;
                                                            atualizarMultiplicador(index, novoMult);
                                                        }}
                                                        style={{ width: '80px' }}
                                                        disabled={isLoading}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                
                                {/* Peças Extras */}
                                {pecasExtras.map((pecaExtra, index) => {
                                    const isReadOnlyExtra = isBatchMode;
                                    // Buscar nome do motivo quando em modo lote
                                    const motivoNome = isReadOnlyExtra && motivoBatch !== -1 
                                        ? motivos.find(m => m.IdMotivo === motivoBatch)?.Motivo || 'Aplicado em lote'
                                        : null;
                                    
                                    return (
                                        <tr key={`extra-${index}`}>
                                            <td>
                                                <Button 
                                                    variant="danger" 
                                                    size="sm"
                                                    onClick={() => removerPecaExtra(index)}
                                                    disabled={isLoading}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    value={pecaExtra.pecaSelecionada?.peca || ''}
                                                    onChange={(e) => {
                                                        const pecaSelecionada = pecasExtrasDisponiveis.find(p => p.peca === e.target.value);
                                                        atualizarPecaExtra(index, 'pecaSelecionada', pecaSelecionada);
                                                    }}
                                                    disabled={isLoading}
                                                    style={{ minWidth: '150px' }}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {Array.isArray(pecasExtrasDisponiveis) && pecasExtrasDisponiveis.length > 0 ? (
                                                        pecasExtrasDisponiveis.map((peca, pIndex) => (
                                                            <option key={pIndex} value={peca.peca}>
                                                                {peca.peca}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value="" disabled>Carregando peças extras...</option>
                                                    )}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                {pecaExtra.pecaSelecionada?.metroForn || '-'}
                                            </td>
                                            <td>
                                                {pecaExtra.pecaSelecionada?.relogio || '-'}
                                            </td>
                                            <td>
                                                {isReadOnlyExtra ? (
                                                    <span className="text-muted">{motivoNome || 'Aplicado em lote'}</span>
                                                ) : (
                                                    <Form.Select
                                                        value={pecaExtra.motivoSelecionado || -1}
                                                        onChange={(e) => {
                                                            atualizarPecaExtra(index, 'motivoSelecionado', e.target.value);
                                                        }}
                                                        disabled={isLoading}
                                                        style={{ minWidth: '150px' }}
                                                    >
                                                        <option value={-1}>Selecione...</option>
                                                        {Array.isArray(motivos) && motivos.map((motivo) => (
                                                            <option key={motivo.IdMotivo} value={motivo.IdMotivo}>
                                                                {motivo.Motivo}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                )}
                                            </td>
                                            <td>
                                                {isReadOnlyExtra ? (
                                                    <span className="text-muted">{quantidadeBatch || '-'}</span>
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={pecaExtra.qtdeLancar || ''}
                                                        onChange={(e) => {
                                                            atualizarPecaExtra(index, 'qtdeLancar', e.target.value);
                                                        }}
                                                        style={{ width: '100px' }}
                                                        disabled={isLoading}
                                                        placeholder="0"
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {isReadOnlyExtra ? (
                                                    <span className="text-muted">{multiplicadorBatch || 1}</span>
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        value={pecaExtra.multiplicador || 1}
                                                        onChange={(e) => {
                                                            atualizarPecaExtra(index, 'multiplicador', parseInt(e.target.value) || 1);
                                                        }}
                                                        style={{ width: '80px' }}
                                                        disabled={isLoading}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        <small className="text-muted d-block mb-3">
                            * O multiplicador cria N registros separados com a mesma quantidade de perda
                        </small>
                        <Button 
                            variant="success" 
                            size="sm"
                            onClick={adicionarPecaExtra}
                            disabled={isLoading}
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
                            Adicionar Peça
                        </Button>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <Form.Group className="mb-3">
                                <Form.Label>Observação</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={observacao}
                                    onChange={handleObservacaoChange}
                                    placeholder="Descreva as observações sobre o lançamento de perdas (opcional)"
                                    disabled={isLoading}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    {Array.isArray(pecas) && pecas.length === 0 && pecasExtras.length === 0 && (
                        <Alert variant="warning">
                            <strong>Atenção:</strong> Não há peças disponíveis para lançamento.
                        </Alert>
                    )}
                    
                </Modal.Body>

                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={fecharModal}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    
                    <Button 
                        variant="primary" 
                        type="submit"
                        disabled={isLoading || ((!Array.isArray(pecas) || pecas.length === 0) && pecasExtras.length === 0)}
                    >
                        {isLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processando...
                            </>
                        ) : (
                            'Lançar Perdas'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalLancamentoPerdasLaboratorio;

